import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ImpersonationBanner from "./ImpersonationBanner";
import { LayoutDashboard, Users, ListChecks, LogOut, Sprout, UserRound, Shield, Settings as SettingsIcon, MapPin, CalendarDays } from "lucide-react";

const STABLE_NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/horses",    icon: Sprout,          label: "Horses",    testid: "nav-horses" },
  { to: "/trips",     icon: MapPin,          label: "Trips",     testid: "nav-trips" },
  { to: "/scheduler", icon: CalendarDays,    label: "Scheduler", testid: "nav-scheduler" },
  { to: "/owners",    icon: UserRound,       label: "Owners",    testid: "nav-owners" },
  { to: "/staff",     icon: Users,           label: "Staff",     testid: "nav-staff" },
  { to: "/jobs",      icon: ListChecks,      label: "Jobs",      testid: "nav-jobs" },
  { to: "/settings",  icon: SettingsIcon,    label: "Settings",  testid: "nav-settings" },
];

const SUPERADMIN_NAV = [
  { to: "/admin", icon: Shield, label: "Super Admin", testid: "nav-superadmin" },
];

const OWNER_NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/horses", icon: Sprout, label: "My Horses", testid: "nav-horses" },
  { to: "/trips",  icon: MapPin, label: "Trips",     testid: "nav-trips" },
  { to: "/scheduler", icon: CalendarDays, label: "Scheduler", testid: "nav-scheduler" },
];

// Minimal-access service roles — see horses (read-only) and their own profile.
const MINIMAL_NAV = [
  { to: "/horses", icon: Sprout, label: "Horses", testid: "nav-horses" },
];
const MINIMAL_ROLES = new Set(["handyman", "cleaner", "gardener"]);

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isSuperAdmin = user?.role === "super_admin" && !user?.is_impersonating;
  const isOwnerClient = user?.role === "owner";
  const isMinimal = MINIMAL_ROLES.has(user?.role);
  const NAV = isSuperAdmin
    ? SUPERADMIN_NAV
    : isOwnerClient
    ? OWNER_NAV
    : isMinimal
    ? MINIMAL_NAV
    : STABLE_NAV;

  return (
    <div className="app-shell flex min-h-screen">
      {/* Sidebar - desktop */}
      <aside
        className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-64 px-4 py-6"
        style={{ background: "#3B2F1E", color: "#F5EFE6" }}
        data-testid="app-sidebar"
      >
        <div className="flex items-center gap-3 px-2 mb-8">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: "#7C5C3A" }}
          >
            <Sprout size={20} color="#F5EFE6" />
          </div>
          <div>
            <div className="font-heading leading-none" style={{ color: "#F5EFE6", fontSize: 24, letterSpacing: "-0.02em" }}>
              StablOS
            </div>
            <div className="text-xs mt-1" style={{ color: "#D4A96A", letterSpacing: "0.08em", textTransform: "uppercase" }}>Equine ERP</div>
          </div>
        </div>

        <nav className="flex flex-col gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `se-sidebar-item ${isActive ? "active" : ""}`}
              data-testid={item.testid}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto">
          <div className="se-divider mb-4" style={{ background: "rgba(245,239,230,0.12)" }} />
          <div className="px-3 mb-3">
            <div className="text-sm font-medium" style={{ color: "#F5EFE6" }} data-testid="sidebar-user-name">
              {user?.full_name}
            </div>
            <div className="text-xs" style={{ color: "#D4A96A" }} data-testid="sidebar-user-role">
              {user?.role}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="se-sidebar-item w-full"
            data-testid="logout-button"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content. Bottom padding clears the mobile nav (64px) + badge zone (64px). */}
      <main className="flex-1 md:ml-64 pb-36 md:pb-8">
        <ImpersonationBanner />
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 md:py-10">
          {children}
        </div>
      </main>

      {/* Bottom nav - mobile.
          Icons live in the TOP 64px of this bar. The bottom 64px is a reserved
          background "skirt" so the platform "Made with Emergent" badge sits below
          the nav icons (not over them) regardless of badge width. */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-20"
        style={{
          background: "#FDFAF6",
          borderTop: "0.5px solid rgba(212, 169, 106, 0.3)",
          paddingBottom: 64,
        }}
        data-testid="mobile-bottom-nav"
      >
        <div className="flex justify-around items-center" style={{ height: 64 }}>
        {NAV.slice(0, 5).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 px-3 py-2 min-h-[44px] min-w-[44px] ${
                isActive ? "" : ""
              }`
            }
            style={({ isActive }) => ({ color: isActive ? "#7C5C3A" : "#8C9E8A" })}
            data-testid={`mobile-${item.testid}`}
          >
            <item.icon size={20} />
            <span className="text-xs font-medium">{item.label}</span>
          </NavLink>
        ))}
        </div>
      </nav>
    </div>
  );
}
